import React, { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { RequestStatusBar } from '@/components/generic/RequestStatusBar';
import { IconClose } from '@/components/icons/IconClose';
import { ContainerWithCurtain } from '@/components/layout/ContainerWithCurtain';
import { Modal, ModalBody } from '@/components/proxy/reactstrap/Modal';
import { ContactVm } from '@/types';
import { formatPhoneNumber } from '@/utils/format-phone-number';
import { removeNonNumeric } from '@/utils/remove-non-numeric';

import { AddressForm } from './components/AddressForm';
import { AddressList } from './components/AddressList';
import { ConfirmationBanner } from './components/ConfirmationBanner';
import { AddressBookModalViews, ContactAddress, Props } from './types';

import styles from './AddressBook.module.scss';

export const AddressBookModal = (props: Props) => {
  const {
    isModalOpen,
    shippingAddresses,
    selectedAddress,
    initialModalView,
    createAddressError,
    updateAddressError,
    deleteAddressError,
    loadingFetch,
    loadingCreate,
    loadingUpdate,
    loadingDelete,
    closeModal,
    selectAddress,
    createShippingAddress,
    updateShippingAddress,
    deleteShippingAddress,
  } = props;

  const usePreviousProps = (value: boolean) => {
    const ref = useRef<boolean>(false);

    useEffect(() => {
      ref.current = value;
    });

    return ref.current;
  };

  const [formError, setFormError] = useState<string>('');
  const [address, setAddress] = useState<ContactVm | null>(null);
  const [modalView, setModalView] = useState<AddressBookModalViews>(initialModalView);

  // set the modal view to its initial state when the modal opens
  useEffect(() => {
    if (isModalOpen) {
      setModalView(initialModalView);
    }
  }, [isModalOpen]);

  useEffect(() => {
    setFormError(createAddressError ? 'Unable to submit the address' : '');
  }, [createAddressError]);

  useEffect(() => {
    setFormError(updateAddressError ? 'Unable to update the address' : '');
  }, [updateAddressError]);

  useEffect(() => {
    setFormError(deleteAddressError ? 'Unable to delete the address' : '');
  }, [deleteAddressError]);

  const openForm = (contactToEdit?: ContactVm | null) => {
    setFormError('');
    setAddress(contactToEdit ?? null);
    setModalView(AddressBookModalViews.AddressForm);
  };

  const closeForm = () => {
    setModalView(AddressBookModalViews.AddressList);
    setFormError('');
  };

  const cancelForm = () => {
    setModalView(AddressBookModalViews.AddressList);
    setFormError('');
  };

  const deleteAddress = (contactToDelete?: ContactVm | null) => {
    if (contactToDelete) {
      setAddress(contactToDelete);
      setModalView(AddressBookModalViews.DeleteAddressConfirmation);
    }
  };

  const onAddressSubmit = (contact: ContactVm) => {
    if (contact.phoneNumber) {
      contact.phoneNumber = removeNonNumeric(contact.phoneNumber);
    }
    if (contact.contactId) {
      updateShippingAddress(contact);

      const isSelectedAddress = selectedAddress?.contactId === contact.contactId;

      if (isSelectedAddress) {
        selectAddress(contact);
      }
    } else {
      createShippingAddress(contact);
    }
  };

  const onAddressDelete = (contact: ContactVm | null) => {
    if (contact?.contactId) {
      deleteShippingAddress(contact.contactId);
    }
  };

  const isActionsLoading = loadingCreate || loadingUpdate || loadingDelete;
  const isLoading = loadingFetch || isActionsLoading;

  const prevLoadingProps: boolean = usePreviousProps(isLoading);

  useEffect(() => {
    if (!prevLoadingProps || !isEqual(isActionsLoading, prevLoadingProps)) {
      closeForm();
      setFormError('');
    }
  }, [isActionsLoading]);

  const addresses = shippingAddresses.map(
    (contact: ContactVm): ContactAddress => ({
      title: `${contact.firstName} ${contact.lastName}`,
      isDefault: contact.isDefault,
      isSavedAddress: true,
      contact: {
        ...contact,
        phoneNumber: formatPhoneNumber(contact?.phoneNumber) ?? '',
      },
    }),
  );

  return (
    <Modal
      backdrop="static"
      className={styles.modal_dialog}
      fullscreen
      isOpen={isModalOpen}
      keyboard={false}
      modalClassName={styles.modal}
      toggle={closeModal}
    >
      <ModalBody>
        <ContainerWithCurtain showCurtain={isLoading} transparent>
          <IconClose
            classes={{ container: styles.close }}
            data-testid="address-book-close-icon"
            onClick={closeModal}
          />
          {modalView === AddressBookModalViews.AddressList && (
            <AddressList
              addresses={addresses}
              closeModal={closeModal}
              deleteAddress={deleteAddress}
              deleteError={formError}
              initiallySelectedAddress={selectedAddress}
              openForm={openForm}
              selectAddress={selectAddress}
            />
          )}
          {modalView === AddressBookModalViews.AddressForm && (
            <AddressForm
              addressToEdit={address}
              cancel={cancelForm}
              isShippingAddressListEmpty={shippingAddresses.length === 0}
              submit={onAddressSubmit}
              submitError={formError}
            />
          )}
          {modalView === AddressBookModalViews.DeleteAddressConfirmation && (
            <ConfirmationBanner
              cancelTitle="Cancel, return to address book"
              onCancelButtonClick={() => {
                setModalView(AddressBookModalViews.AddressList);
                setAddress(null);
              }}
              onSubmitButtonClick={() => {
                setModalView(AddressBookModalViews.AddressList);
                onAddressDelete(address);
                setAddress(null);
              }}
              subTitle="This address will be permanently deleted from your account. You will have to re-enter the address again should you need it. "
              submitTitle="I understand, delete this address"
              title="Are you sure you want to delete this address?"
            />
          )}
        </ContainerWithCurtain>
        <RequestStatusBar
          classes={{ container: styles.modal_progress }}
          loading={isLoading}
          messages={{
            inProgress: 'Loading',
            error: '',
          }}
        />
      </ModalBody>
    </Modal>
  );
};
